import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Layouts
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';
import AdminPanelLayout from './components/layout/AdminPanelLayout';

// Route Guards
import PrivateRoute from './routes/PrivateRoute';
import AdminPanelRoute from './routes/AdminPanelRoute';

// Common Components
import Loader from './components/common/Loader';
import PageLoader from './components/common/PageLoader';

// Admin Pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminPdfPage = lazy(() => import('./pages/admin/AdminPdfPage'));
const WithdrawFundPage = lazy(() => import('./pages/admin/WithdrawFundPage'));

// Admin Panel Home Pages
const RecentCampaignsPage = lazy(() => import('./pages/adminPanel/home/RecentCampaignsPage'));
const StoriesPage = lazy(() => import('./pages/adminPanel/home/StoriesPage'));
const DiscoverPage = lazy(() => import('./pages/adminPanel/home/DiscoverPage'));

// Admin Panel Reports Pages
const SalesReportsPage = lazy(() => import('./pages/adminPanel/reports/SalesReportsPage'));
const OrganizationReportsPage = lazy(() => import('./pages/adminPanel/reports/OrganizationReportsPage'));
const FundraiserDetailPage = lazy(() => import('./pages/adminPanel/reports/FundraiserDetailPage'));

// Admin Panel Stripe Pages
const StripeCredentialPage = lazy(() => import('./pages/adminPanel/stripe/StripeCredentialPage'));
const PaymentPlanPage = lazy(() => import('./pages/adminPanel/stripe/PaymentPlanPage'));

// Admin Panel Pages
const ActivityAdministratorsPage = lazy(() => import('./pages/adminPanel/ActivityAdministratorsPage'));
const ActivityIndividualsPage = lazy(() => import('./pages/adminPanel/ActivityIndividualsPage'));
const ActivityParticipantsPage = lazy(() => import('./pages/adminPanel/ActivityParticipantsPage'));
const AdminPanelChangePasswordPage = lazy(() => import('./pages/adminPanel/AdminPanelChangePasswordPage'));
const AdminPanelCreatePasswordPage = lazy(() => import('./pages/adminPanel/AdminPanelCreatePasswordPage'));
const AdminPanelDashboard = lazy(() => import('./pages/adminPanel/AdminPanelDashboard'));
const AdminPanelForgotPasswordPage = lazy(() => import('./pages/adminPanel/AdminPanelForgotPasswordPage'));
const AdminPanelLoginPage = lazy(() => import('./pages/adminPanel/AdminPanelLoginPage'));
const AdminPanelOtpVerificationPage = lazy(() => import('./pages/adminPanel/AdminPanelOtpVerificationPage'));
const AmbassadorCodesPage = lazy(() => import('./pages/adminPanel/AmbassadorCodesPage'));
const RolesPermissionsPage = lazy(() => import('./pages/adminPanel/RolesPermissionsPage'));
const TeamDirectoryPage = lazy(() => import('./pages/adminPanel/TeamDirectoryPage'));
const SystemBlueprintPage = lazy(() => import('./pages/roles/SystemBlueprintPage'));
const DonorsUsersPage = lazy(() => import('./pages/adminPanel/DonorsUsersPage'));
const OrganizationsPage = lazy(() => import('./pages/adminPanel/OrganizationsPage'));
const SalespersonPage = lazy(() => import('./pages/adminPanel/SalespersonPage'));
const UsersPage = lazy(() => import('./pages/adminPanel/UsersPage'));
const WithdrawRequestPage = lazy(() => import('./pages/adminPanel/WithdrawRequestPage'));

// Auth Pages
const AdminSignupPage = lazy(() => import('./pages/auth/AdminSignupPage'));
const ChangePasswordPage = lazy(() => import('./pages/auth/ChangePasswordPage'));
const ChooseRolePage = lazy(() => import('./pages/auth/ChooseRolePage'));
const CreatePasswordPage = lazy(() => import('./pages/auth/CreatePasswordPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const IndividualSignupPage = lazy(() => import('./pages/auth/IndividualSignupPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const OtpVerificationPage = lazy(() => import('./pages/auth/OtpVerificationPage'));
const ParticipantRegisterPage = lazy(() => import('./pages/auth/ParticipantRegisterPage'));
const ParticipantSignupPage = lazy(() => import('./pages/auth/ParticipantSignupPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const RolePage = lazy(() => import('./pages/auth/RolePage'));

// Home Pages
const AboutAdminPage = lazy(() => import('./pages/home/AboutAdminPage'));
const AboutParticipantPage = lazy(() => import('./pages/home/AboutParticipantPage'));
const AboutUsPage = lazy(() => import('./pages/home/AboutUsPage'));
const ContactUsPage = lazy(() => import('./pages/home/ContactUsPage'));
const FundMeWizAppPage = lazy(() => import('./pages/home/FundMeWizAppPage'));
const HomePage = lazy(() => import('./pages/home/HomePage'));
const MemberLandingPage = lazy(() => import('./pages/home/MemberLandingPage'));
const NotFoundPage = lazy(() => import('./pages/home/NotFoundPage'));
const ParticipantLandingPage = lazy(() => import('./pages/home/ParticipantLandingPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/home/PrivacyPolicyPage'));
const StartCampaignPage = lazy(() => import('./pages/home/StartCampaignPage'));
const StaticPages = lazy(() => import('./pages/home/StaticPages'));
const TermsOfUsePage = lazy(() => import('./pages/home/TermsOfUsePage'));
const TravelPage = lazy(() => import('./pages/home/TravelPage'));

// Campaign pages
const CampaignBrowsePage = lazy(() => import('./pages/campaign/CampaignBrowsePage'));
const CampaignPublicPage = lazy(() => import('./pages/campaign/CampaignPublicPage'));
const CreateCampaignPage = lazy(() => import('./pages/campaign/CreateCampaignPage'));
const GratitudeWallPage = lazy(() => import('./pages/campaign/GratitudeWallPage'));

// Role dashboards
const OrgOwnerDashboard = lazy(() => import('./pages/roles/OrgOwnerDashboard'));
const TeamCaptainDashboard = lazy(() => import('./pages/roles/TeamCaptainDashboard'));
const VolunteerDashboard = lazy(() => import('./pages/roles/VolunteerDashboard'));
const IndividualDashboard = lazy(() => import('./pages/individual/IndividualDashboard'));
const IndividualWithdrawFund = lazy(() => import('./pages/individual/IndividualWithdrawFund'));

const LandingPage = lazy(() => import('./pages/participant/LandingPage'));
const ParticipantDashboard = lazy(() => import('./pages/participant/ParticipantDashboard'));

function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes with Main Layout */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/travel" element={<TravelPage />} />
        </Route>

        {/* Landing Pages (No Layout - Self-contained) */}
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        <Route path="/contact-us" element={<ContactUsPage />} />
        <Route path="/about-admin" element={<AboutAdminPage />} />
        <Route path="/fundmewiz-app" element={<FundMeWizAppPage />} />
        <Route path="/member-landing-page" element={<MemberLandingPage />} />
        <Route path="/participant-landing-page" element={<ParticipantLandingPage />} />
        <Route path="/about-participant" element={<AboutParticipantPage />} />
        <Route path="/start-campaign" element={<StartCampaignPage />} />
        <Route path="/campaigns" element={<CampaignBrowsePage />} />
        <Route path="/campaign/:slug" element={<CampaignPublicPage />} />
        <Route path="/gratitude-wall" element={<GratitudeWallPage />} />
        <Route path="/choose-role" element={<ChooseRolePage />} />
        <Route path="/about-us" element={<AboutUsPage />} />

        {/* Participant Registration (No Layout - Self-contained) */}
        <Route path="/participant" element={<ParticipantRegisterPage />} />
        <Route path="/admin" element={<AdminSignupPage />} />
        <Route path="/individual" element={<ParticipantSignupPage />} />
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          {/* Friendly login URLs */}
          <Route path="/superadmin-login" element={<LoginPage />} />
          <Route path="/admin-login" element={<LoginPage />} />
          <Route path="/participant-login" element={<LoginPage />} />
          <Route path="/individual-login" element={<LoginPage />} />
          <Route path="/orgowner-login" element={<LoginPage />} />
          <Route path="/captain-login" element={<LoginPage />} />
          <Route path="/volunteer-login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/role" element={<RolePage />} />

          <Route path="/admin-signup" element={<AdminSignupPage />} />
          <Route path="/participant" element={<ParticipantRegisterPage />} />
          <Route path="/participant-signup" element={<ParticipantSignupPage />} />
          <Route path="/individual-signup" element={<IndividualSignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/otp-verification" element={<OtpVerificationPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/create-password" element={<CreatePasswordPage />} />
        </Route>

        {/* Admin Panel Auth Routes (No Layout) */}
        <Route path="/admin-panel" element={<Navigate to="/admin-panel/login" replace />} />
        <Route path="/admin-panel/login" element={<AdminPanelLoginPage />} />
        <Route path="/admin-panel/forgot-password" element={<AdminPanelForgotPasswordPage />} />
        <Route path="/admin-panel/otp-verification" element={<AdminPanelOtpVerificationPage />} />
        <Route path="/admin-panel/create-password" element={<AdminPanelCreatePasswordPage />} />
        <Route path="/admin-panel/change-password" element={<AdminPanelChangePasswordPage />} />

        {/* Admin Panel Protected Routes */}
        <Route element={<AdminPanelRoute><AdminPanelLayout /></AdminPanelRoute>}>
          <Route path="/admin-panel/dashboard" element={<AdminPanelDashboard />} />
          <Route path="/admin-panel/organizations" element={<OrganizationsPage />} />
          <Route path="/admin-panel/activity-administrators" element={<ActivityAdministratorsPage />} />
          <Route path="/admin-panel/activity-participants" element={<ActivityParticipantsPage />} />
          <Route path="/admin-panel/activity-individuals" element={<ActivityIndividualsPage />} />
          <Route path="/admin-panel/donors-users" element={<DonorsUsersPage />} />
          <Route path="/admin-panel/withdraw-request" element={<WithdrawRequestPage />} />
          <Route path="/admin-panel/user" element={<UsersPage />} />
          <Route path="/admin-panel/ambassador-codes" element={<AmbassadorCodesPage />} />
          <Route path="/admin-panel/salesperson" element={<SalespersonPage />} />

          {/* Home Tab Pages */}
          <Route path="/admin-panel/home/recent-campaigns" element={<RecentCampaignsPage />} />
          <Route path="/admin-panel/home/stories" element={<StoriesPage />} />
          <Route path="/admin-panel/home/discover" element={<DiscoverPage />} />

          {/* Stripe Pages */}
          <Route path="/admin-panel/stripe/stripe-credential" element={<StripeCredentialPage />} />
          <Route path="/admin-panel/stripe/payment-plan" element={<PaymentPlanPage />} />

          {/* Reports Pages */}
          <Route path="/admin-panel/reports/sales-reports" element={<SalesReportsPage />} />
          <Route path="/admin-panel/reports/organization-reports" element={<OrganizationReportsPage />} />
          <Route path="/admin-panel/reports/fundraiser-detail" element={<FundraiserDetailPage />} />

        </Route>

        {/* User Dashboard Routes - Self-contained (include their own headers) */}
        <Route path="/admin-dashboard" element={<PrivateRoute roles={[1, 2]}><AdminDashboard /></PrivateRoute>} />
        <Route path="/team-directory" element={<PrivateRoute roles={[1]}><TeamDirectoryPage /></PrivateRoute>} />
        <Route path="/system-blueprint" element={<PrivateRoute roles={[1]}><SystemBlueprintPage /></PrivateRoute>} />
        <Route path="/roles-permissions" element={<PrivateRoute roles={[1]}><RolesPermissionsPage /></PrivateRoute>} />
        <Route path="/withdraw-fund" element={<PrivateRoute><WithdrawFundPage /></PrivateRoute>} />
        <Route path="/individual-dashboard" element={<PrivateRoute roles={[4]}><IndividualDashboard /></PrivateRoute>} />
        <Route path="/individual/withdraw-fund" element={<PrivateRoute roles={[4]}><IndividualWithdrawFund /></PrivateRoute>} />
        <Route path="/participant-dashboard" element={<PrivateRoute roles={[3]}><ParticipantDashboard /></PrivateRoute>} />
        <Route path="/admin-pdf" element={<PrivateRoute roles={[1, 2]}><AdminPdfPage /></PrivateRoute>} />
        <Route path="/create-campaign" element={<PrivateRoute><CreateCampaignPage /></PrivateRoute>} />
        <Route path="/org-owner-dashboard" element={<PrivateRoute roles={[5]}><OrgOwnerDashboard /></PrivateRoute>} />
        <Route path="/team-captain-dashboard" element={<PrivateRoute roles={[6]}><TeamCaptainDashboard /></PrivateRoute>} />
        <Route path="/volunteer-dashboard" element={<PrivateRoute roles={[7]}><VolunteerDashboard /></PrivateRoute>} />

        {/* Other protected routes with MainLayout */}
        <Route element={<MainLayout />}>
          <Route path="/landing-page/:code" element={<LandingPage />} />
        </Route>

        {/* 404 Page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
